import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Input } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { JustificationUpload } from '@/components/banking/JustificationUpload'
import { apiErrorMessage } from '@/lib/api-error'
import { bankingApi } from '@/lib/banking-api'
import { isValidIban } from '@/lib/iban'
import type { PreflightDto } from '@/lib/types'

const FLAG_KEYS: Record<string, string> = {
  unusual_amount: 'preflight.flag_unusual_amount',
  new_beneficiary: 'preflight.flag_new_beneficiary',
  high_frequency: 'preflight.flag_high_frequency',
}

export function Transfer() {
  const { t } = useTranslation('banking')
  const navigate = useNavigate()
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: () => bankingApi.listAccounts() })
  const account = accountsQuery.data?.items[0]

  const [step, setStep] = useState<1 | 2>(1)
  const [name, setName] = useState('')
  const [iban, setIban] = useState('')
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  const [ibanError, setIbanError] = useState<string | null>(null)
  const [transferId, setTransferId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [preflight, setPreflight] = useState<PreflightDto | null>(null)

  async function doInitiate() {
    if (!account) return
    const cents = Math.round(Number.parseFloat(amount.replace(',', '.')) * 100)
    const res = await bankingApi.initiateTransfer({
      account_id: account.id,
      amount_cents: cents,
      currency: account.currency,
      counterparty_name: name,
      counterparty_iban: iban,
      ...(reference ? { reference } : {}),
    })
    setTransferId(res.transfer_id)
  }

  async function proceed() {
    setError(null)
    setLoading(true)
    try {
      await doInitiate()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  function goToAmount(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!isValidIban(iban)) {
      setIbanError(t('transfer.invalid_iban'))
      return
    }
    setIbanError(null)
    setStep(2)
  }

  async function onSend(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!account) return
    setError(null)
    setLoading(true)
    try {
      if (transferId) {
        await bankingApi.confirmTransfer(transferId, code)
        navigate('/app/transfer/success', { state: { name } })
      } else if (preflight) {
        // user already saw the review panel and chose to proceed
        await doInitiate()
      } else {
        // USP #2 — pre-flight check before initiating
        const cents = Math.round(Number.parseFloat(amount.replace(',', '.')) * 100)
        const pf = await bankingApi.preflight({ amount_cents: cents, counterparty_iban: iban })
        setPreflight(pf)
        if (pf.outcome === 'pre_approved') await doInitiate()
        // requires_review / escalated → render the panel below, stop here
      }
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mx-auto max-w-md">
      <Card>
        <h1 className="text-2xl font-semibold">{t('transfer.title')}</h1>

        {step === 1 ? (
          <form className="mt-6 flex flex-col gap-4" onSubmit={goToAmount}>
            <Input
              label={t('transfer.recipient_name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label={t('transfer.recipient_iban')}
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              {...(ibanError ? { error: ibanError } : {})}
              required
            />
            <Button type="submit">{t('transfer.continue')}</Button>
          </form>
        ) : preflight?.outcome === 'escalated' ? (
          <div className="mt-6 flex flex-col gap-4">
            <h2 className="text-lg font-medium">{t('preflight.escalated_title')}</h2>
            <p className="text-text-muted">{t('preflight.escalated_body')}</p>
            <Button variant="secondary" onClick={() => navigate('/app')}>
              {t('preflight.back')}
            </Button>
          </div>
        ) : preflight?.outcome === 'requires_review' && !transferId ? (
          <div className="mt-6 flex flex-col gap-4">
            <h2 className="text-lg font-medium">{t('preflight.review_title')}</h2>
            <p className="text-text-muted">{t('preflight.review_intro')}</p>
            <ul className="list-inside list-disc text-sm text-text-muted">
              {preflight.flags.map((f) =>
                FLAG_KEYS[f] ? <li key={f}>{t(FLAG_KEYS[f])}</li> : null,
              )}
            </ul>
            <JustificationUpload assessmentId={preflight.assessment_id} />
            {error ? <ErrorBanner message={error} /> : null}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setStep(1)
                  setPreflight(null)
                }}
                disabled={loading}
              >
                {t('preflight.back')}
              </Button>
              <Button onClick={proceed} disabled={loading} withSignatureDot>
                {t('preflight.proceed')}
              </Button>
            </div>
          </div>
        ) : (
          <form className="mt-6 flex flex-col gap-4" onSubmit={onSend}>
            <Input
              label={t('transfer.amount')}
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              disabled={transferId !== null}
            />
            <Input
              label={t('transfer.reference')}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              disabled={transferId !== null}
            />

            {transferId ? (
              <Input
                label={t('transfer.code_label')}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                hint={t('transfer.confirm_2fa')}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            ) : null}

            {error ? <ErrorBanner message={error} /> : null}

            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setStep(1)} disabled={loading}>
                {t('transfer.back')}
              </Button>
              <Button type="submit" disabled={loading} withSignatureDot={transferId !== null}>
                {transferId ? t('transfer.send') : t('transfer.continue')}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  )
}
